import { ownerRoleIsUnassigned } from './incidentPatternVocabulary';

export const FOLLOW_UP_MEASUREMENT_OUTCOME_OPTIONS = [
  { value: 'improved', label: 'Improved' },
  { value: 'no_change', label: 'No change' },
  { value: 'worse', label: 'Worse' },
  { value: 'insufficient_data', label: 'Insufficient data' },
  { value: 'not_measured', label: 'Not measured' },
] as const;

export type FollowUpMeasurementOutcomeValue =
  (typeof FOLLOW_UP_MEASUREMENT_OUTCOME_OPTIONS)[number]['value'];

export const TRACK_IMPACT_PANEL_DESCRIPTION =
  'After follow-up action has been taken, record whether the recurring risk pattern improved.';

export const TRACK_IMPACT_UNAVAILABLE_MESSAGE =
  'Impact measurement is available after an action owner is assigned and follow-up begins.';

export const TRACK_IMPACT_MEASUREMENT_HELPER =
  'Use this after action has been taken. Do not mark a risk pattern reduced from review alone.';

export function formatMeasurementOutcomeLabel(value: string | null | undefined): string {
  const raw = String(value || '').trim().toLowerCase();
  const match = FOLLOW_UP_MEASUREMENT_OUTCOME_OPTIONS.find((option) => option.value === raw);
  if (match) return match.label;
  return raw ? raw.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) : 'Not measured';
}

export type ImpactTrackingSummary = {
  baselineCount: number | null;
  followUpCount: number | null;
  reductionPercent: number | null;
  outcomeLabel: string;
  conclusion: string;
};

export function buildImpactTrackingSummary(args: {
  baselineCount?: number | null;
  followUpCount?: number | null;
  outcomeStatus?: string | null;
  readinessState?: string | null;
  actionEffectiveAt?: string | null;
}): ImpactTrackingSummary {
  const baseline = Number.isFinite(Number(args.baselineCount)) ? Number(args.baselineCount) : null;
  const followUp = Number.isFinite(Number(args.followUpCount)) ? Number(args.followUpCount) : null;
  const outcomeLabel = formatMeasurementOutcomeLabel(args.outcomeStatus);
  const readinessState = String(args.readinessState || '').trim().toLowerCase();
  const actionEffectiveAt = String(args.actionEffectiveAt || '').trim();

  const reductionPercent = baseline != null && followUp != null && baseline > 0
    ? Math.round(((baseline - followUp) / baseline) * 100)
    : null;

  let conclusion = 'Impact measurement not recorded';
  if (readinessState === 'waived') {
    conclusion = 'Impact measurement waived by operator';
  } else if (readinessState === 'tracking_in_progress' || (!!actionEffectiveAt && outcomeLabel === 'Not measured' && reductionPercent == null)) {
    conclusion = 'Measurement in progress after corrective action';
  } else if (readinessState === 'insufficient_data') {
    conclusion = 'More monitoring is needed before reaching a conclusion';
  } else if (outcomeLabel !== 'Not measured') {
    if (String(args.outcomeStatus || '').trim().toLowerCase() === 'improved') {
      conclusion = 'Corrective action appears effective';
    } else if (String(args.outcomeStatus || '').trim().toLowerCase() === 'no_change') {
      conclusion = 'No measurable change yet';
    } else if (String(args.outcomeStatus || '').trim().toLowerCase() === 'worse') {
      conclusion = 'Risk frequency increased after corrective action';
    } else if (String(args.outcomeStatus || '').trim().toLowerCase() === 'insufficient_data') {
      conclusion = 'More monitoring is needed before reaching a conclusion';
    }
  } else if (baseline != null && followUp != null) {
    if (followUp < baseline) conclusion = 'Corrective action appears effective';
    else if (followUp === baseline) conclusion = 'No measurable change yet';
    else conclusion = 'Risk frequency increased after corrective action';
  }

  return {
    baselineCount: baseline,
    followUpCount: followUp,
    reductionPercent,
    outcomeLabel,
    conclusion,
  };
}

export function readIncidentMeasurementFields(
  incident: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const envelope = ((incident as { envelope_metadata?: { measurement?: Record<string, unknown> } })
    ?.envelope_metadata?.measurement || {}) as Record<string, unknown>;
  const safety = ((incident as { safety_case?: { outcome_measurement?: Record<string, unknown> } })
    ?.safety_case?.outcome_measurement || {}) as Record<string, unknown>;
  return { ...safety, ...envelope };
}

/** Operator waived impact measurement for safety report readiness (Epic 94h). */
export function readImpactMeasurementWaived(
  incident: Record<string, unknown> | null | undefined,
): boolean {
  const measurement = readIncidentMeasurementFields(incident);
  const raw = measurement.impact_waived;
  if (raw === true) return true;
  if (raw === false) return false;
  const text = String(raw || '').trim().toLowerCase();
  return text === 'true' || text === '1' || text === 'yes';
}

/** True when an explicit follow-up measurement was saved on the representative incident. */
export function isExplicitFollowUpMeasurementRecorded(
  incident: Record<string, unknown> | null | undefined,
): boolean {
  if (readImpactMeasurementWaived(incident)) return true;
  const measurement = readIncidentMeasurementFields(incident);
  const outcomeStatus = String(measurement.outcome_status || '').trim().toLowerCase();
  if (outcomeStatus && outcomeStatus !== 'not_measured') return true;
  if (String(measurement.measured_at || '').trim()) return true;

  const numericFields = [
    measurement.baseline_count,
    measurement.follow_up_count,
    measurement.baseline_recurrence_count,
    measurement.follow_up_recurrence_count,
  ];
  if (numericFields.some((value) => value != null && value !== '' && Number.isFinite(Number(value)))) {
    return true;
  }

  if (measurement.measurement_available === true) return true;
  if (String(measurement.readiness_state || '').trim().toLowerCase() === 'measured') return true;
  return false;
}

export function isActionPlanStartedForImpactTracking(args: {
  followUpOwnerLabel?: string | null;
  opsState?: string | null;
  incidentStatus?: string | null;
  hasWorkOrder?: boolean;
  hasNextReview?: boolean;
}): boolean {
  if (ownerRoleIsUnassigned(args.followUpOwnerLabel)) return false;

  const opsState = String(args.opsState || 'new').trim().toLowerCase();
  if (opsState && opsState !== 'new') return true;

  const incidentStatus = String(args.incidentStatus || 'OPEN').trim().toUpperCase();
  if (incidentStatus && incidentStatus !== 'OPEN') return true;

  if (args.hasWorkOrder) return true;
  if (args.hasNextReview) return true;
  return false;
}

export function isTrackImpactMeasurementReady(args: {
  followUpOwnerLabel?: string | null;
  opsState?: string | null;
  incidentStatus?: string | null;
  hasWorkOrder?: boolean;
  hasNextReview?: boolean;
}): boolean {
  return isActionPlanStartedForImpactTracking(args);
}
